/**
 * 设置页：外观主题、账户信息、登录密码、保险库主密码/恢复码、本机 KDF 缓存、登出。
 * 请求体严格白名单：ChangePasswordBody 只含 currentPassword/newPassword；
 * VaultChangePasswordBody 由 lib/vault 内部拼装（currentAuthHash/authHash/kdfParams）。
 */
import { useState } from 'react';
import { changeLoginPassword, setAccessToken } from '../lib/api';
import type { RecoveryMode } from '../lib/vault';
import { useAuth } from '../stores/auth';
import { useTheme, type ThemeMode } from '../stores/theme';
import { useVault } from '../stores/vault';
import { Icon } from '../components/icons';
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Select,
  errMsg,
} from '../components/ui';

/** 卡片内标题（title + 描述） */
function Head({ title, desc }: { title: string; desc?: string }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      {desc && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{desc}</p>}
    </div>
  );
}

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'system', label: '跟随系统' },
  { value: 'light', label: '浅色' },
  { value: 'dark', label: '深色' },
];

const RECOVERY_OPTIONS: Array<{ value: RecoveryMode; label: string; hint: string }> = [
  { value: 'keep', label: '保留现有恢复码', hint: '主密码换新，恢复码继续可用' },
  { value: 'clear', label: '清除恢复码', hint: '换新主密码后不再有恢复码' },
  { value: 'reset', label: '重置恢复码', hint: '生成新恢复码（旧码作废，仅显示一次）' },
];

// ==================== 登录密码 ====================

function LoginPasswordCard() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setOk(false);
    if (newPassword.length < 10 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('新密码至少 10 位，且包含字母和数字');
      return;
    }
    if (newPassword !== confirm) {
      setError('两次输入的新密码不一致');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 严格白名单：只传 DTO 两字段
      const res = await changeLoginPassword({ currentPassword, newPassword });
      setAccessToken(res.accessToken);
      setCurrent('');
      setNew('');
      setConfirm('');
      setOk(true);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <Head title="登录密码" desc="用于登录「追光宝盒」账号" />
      <ErrorBox error={error} className="my-3" />
      {ok && (
        <p className="mb-3 text-sm text-emerald-600 dark:text-emerald-400">登录密码已修改</p>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="当前密码">
          <Input type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label="新密码">
          <Input type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="确认新密码">
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <div className="mt-4">
        <Button loading={busy} onClick={() => void submit()}>
          修改登录密码
        </Button>
      </div>
    </Card>
  );
}

// ==================== 保险库主密码 ====================

function VaultPasswordCard() {
  const changePassword = useVault((s) => s.changePassword);
  const hasLocalKdf = useVault((s) => s.hasLocalKdf);
  const unlocked = useVault((s) => s.unlocked);
  const lock = useVault((s) => s.lock);
  const clearLocalKdf = useVault((s) => s.clearLocalKdf);
  const syncLocalKdf = useVault((s) => s.syncLocalKdf);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [recovery, setRecovery] = useState<RecoveryMode>('keep');
  const [newCode, setNewCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setNewCode(null);
    if (next.length < 10 || !/[A-Za-z]/.test(next) || !/[0-9]/.test(next)) {
      setError('新主密码至少 10 位，且包含字母和数字');
      return;
    }
    if (next !== confirm) {
      setError('两次输入的新主密码不一致');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await changePassword(current, next, recovery);
      setNewCode(res.recoveryCode ?? null);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <Head title="保险库主密码" desc="零知识加密：主密码只在本机派生密钥；更换主密码不会解密/重写已有条目" />
      <ErrorBox error={error} className="my-3" />
      {newCode && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/30">
          <p className="font-medium text-amber-800 dark:text-amber-200">新恢复码（仅显示一次，请立即抄写）：</p>
          <p className="mt-1 select-all font-mono text-base tracking-wider text-amber-900 dark:text-amber-100">{newCode}</p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="当前主密码">
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="新主密码">
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="确认新主密码">
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <div className="mt-3 max-w-sm">
        <Field label="恢复码处理" hint={RECOVERY_OPTIONS.find((o) => o.value === recovery)?.hint}>
          <Select value={recovery} onChange={(e) => setRecovery(e.target.value as RecoveryMode)}>
            {RECOVERY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button loading={busy} onClick={() => void submit()}>
          更换主密码
        </Button>
        {unlocked && (
          <Button variant="secondary" onClick={lock}>
            锁定保险库
          </Button>
        )}
      </div>

      <div className="mt-6 border-t border-slate-100 pt-4 dark:border-slate-700">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500 dark:text-slate-400">本机 KDF 参数缓存：</span>
          <Badge className={hasLocalKdf ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}>
            {hasLocalKdf ? '已缓存' : '未缓存'}
          </Badge>
          <Button size="sm" variant="ghost" onClick={syncLocalKdf}>
            同步缓存
          </Button>
          <Button size="sm" variant="ghost" onClick={clearLocalKdf}>
            清除缓存
          </Button>
          <span className="text-xs text-slate-400">清除后本设备需网络获取参数才能解锁</span>
        </div>
      </div>
    </Card>
  );
}

// ==================== 设置页 ====================

export function SettingsPage() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const mode = useTheme((s) => s.mode);
  const setMode = useTheme((s) => s.setMode);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">设置</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">外观、账户与安全</p>
      </div>

      {/* 外观 */}
      <Card className="p-5">
        <Head title="外观" desc="默认跟随系统，可手动切换深浅色" />
        <div className="mt-3 flex flex-wrap gap-2">
          {THEME_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setMode(o.value)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition ${
                mode === o.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              }`}
            >
              <Icon name={o.value === 'dark' ? 'moon' : 'sun'} className="h-4 w-4" />
              {o.label}
            </button>
          ))}
        </div>
      </Card>

      {/* 账户 */}
      <Card className="p-5">
        <Head title="账户" />
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-slate-500 dark:text-slate-400">昵称</dt>
            <dd className="mt-0.5 font-medium text-slate-800 dark:text-slate-100">{user?.displayName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500 dark:text-slate-400">邮箱</dt>
            <dd className="mt-0.5 font-medium text-slate-800 dark:text-slate-100">{user?.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500 dark:text-slate-400">保险库</dt>
            <dd className="mt-0.5">
              <Badge className={user?.vaultInitialized ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}>
                {user?.vaultInitialized ? '已初始化' : '未初始化'}
              </Badge>
            </dd>
          </div>
        </dl>
        <div className="mt-4">
          <Button variant="danger" onClick={() => void logout()}>
            退出登录
          </Button>
        </div>
      </Card>

      <LoginPasswordCard />
      <VaultPasswordCard />
    </div>
  );
}
